import { trpc } from '../lib/trpc.js';
import { formatGbp } from '../lib/format.js';
import { CapacityBar } from '../components/ui/CapacityBar.js';
import { calculateUnitEconomics, LCC_CONSTANTS } from '@lcc/shared';
import { useState } from 'react';

export function PipelinePage() {
  const { data: thisWeek } = trpc.bookings.thisWeek.useQuery();
  const { data: dashboard } = trpc.metrics.dashboard.useQuery();

  const [cpl, setCpl] = useState<number>(LCC_CONSTANTS.CPL_GREEN_MAX);
  const economics = calculateUnitEconomics({ cplGbp: cpl });

  const projectedWeeklyLeads = dashboard?.yesterday.leads ? dashboard.yesterday.leads * 7 : 0;
  const projectedWeeklyBookings = Math.round(projectedWeeklyLeads * LCC_CONSTANTS.LEAD_TO_BOOKING_RATE);
  const projectedRevenue = projectedWeeklyBookings * LCC_CONSTANTS.AVG_JOB_VALUE_GBP;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl text-navy">Pipeline & Capacity</h1>

      {thisWeek && (
        <div className="card">
          <h2 className="font-display text-xl text-navy mb-4">Current Week</h2>
          <CapacityBar bookings={thisWeek.count} />
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Jobs booked</p>
              <p className="font-mono text-2xl font-medium text-navy">{thisWeek.count}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Revenue</p>
              <p className="font-mono text-2xl font-medium text-navy">{formatGbp(thisWeek.revenue)}</p>
            </div>
          </div>
        </div>
      )}

      {dashboard && (
        <div className="card">
          <h2 className="font-display text-xl text-navy mb-4">Projection (next 7 days)</h2>
          <p className="text-sm text-gray-500 mb-4">Based on yesterday's lead rate × 7-day horizon</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Projected leads</p>
              <p className="font-mono text-xl font-medium text-navy">{projectedWeeklyLeads}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Projected bookings</p>
              <p className="font-mono text-xl font-medium text-navy">{projectedWeeklyBookings}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Projected revenue</p>
              <p className="font-mono text-xl font-medium text-navy">{formatGbp(projectedRevenue)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2 className="font-display text-xl text-navy mb-4">Unit Economics Calculator</h2>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">
            CPL to test: £{cpl}
          </label>
          <input
            type="range"
            min={1}
            max={20}
            step={0.5}
            value={cpl}
            onChange={(e) => setCpl(parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>£1</span><span>£20</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">CPA</p>
            <p className="font-mono text-lg font-medium text-navy">{formatGbp(economics.cpa)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">CAC %</p>
            <p className="font-mono text-lg font-medium text-navy">{Math.round(economics.cacPct * 100)}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Profit/job</p>
            <p className={`font-mono text-lg font-medium ${economics.profitPerJob > 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatGbp(economics.profitPerJob)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Leads/booking</p>
            <p className="font-mono text-lg font-medium text-navy">{economics.leadsPerBooking.toFixed(1)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
